import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardLayout } from '../components/WizardLayout';
import { api } from '../lib/api';
import type { CompanySearchHit } from '@dormant/shared';

// Human labels for Companies House type slugs we don't yet support
// online. Anything not listed here falls back to the raw slug in the
// modal — better to be honest than to guess.
const UNSUPPORTED_TYPE_LABEL: Record<string, string> = {
  'community-interest-company': 'a Community Interest Company (CIC)',
  llp: 'a Limited Liability Partnership (LLP)',
  'limited-partnership': 'a Limited Partnership (LP)',
  'charitable-incorporated-organisation': 'a Charitable Incorporated Organisation (CIO)',
  'scottish-charitable-incorporated-organisation':
    'a Scottish Charitable Incorporated Organisation (SCIO)',
  'royal-charter': 'a Royal Charter company',
  'registered-society-non-jurisdictional': 'a registered society',
  'industrial-and-provident-society': 'an industrial & provident society',
  'unregistered-company': 'an unregistered company',
  'oversea-company': 'an overseas company',
  'plc': 'a Public Limited Company (PLC)',
};

function friendlyTypeName(slug?: string): string {
  if (!slug) return 'this type of company';
  return UNSUPPORTED_TYPE_LABEL[slug] || `a "${slug}" entity`;
}

const DEBOUNCE_MS = 200;

const STATUS_LABEL: Record<string, { label: string; tone: string }> = {
  active: { label: 'Active', tone: 'bg-green-50 text-green-700 ring-green-200' },
  dissolved: { label: 'Dissolved', tone: 'bg-red-50 text-red-700 ring-red-200' },
  liquidation: { label: 'In liquidation', tone: 'bg-amber-50 text-amber-800 ring-amber-200' },
};

function statusPill(s: string) {
  const meta = STATUS_LABEL[s] || { label: s, tone: 'bg-slate-100 text-slate-700 ring-slate-200' };
  return (
    <span
      className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${meta.tone}`}
    >
      {meta.label}
    </span>
  );
}

export function Search() {
  const [q, setQ] = useState('');
  const [items, setItems] = useState<CompanySearchHit[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Shown when we detect a CIC / LLP / charity / etc. — friendlier
  // than a red error banner because it's not the user's mistake, and
  // we want to route them to email rather than dead-end.
  const [unsupportedType, setUnsupportedType] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Debounced search.
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setItems([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = window.setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const res = await api.searchCompanies(q.trim(), ctrl.signal);
        // Defensive: if the API ever returns an unexpected shape (e.g.
        // proxied through Cloudflare with an HTML error page), make sure
        // `items` is always an array so the render branches don't crash
        // on `.length`.
        setItems(Array.isArray(res?.items) ? res.items : []);
        setHighlight(0);
        setOpen(true);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setItems([]);
          setOpen(false);
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [q]);

  // Close dropdown on outside click.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  async function chooseCompany(hit: CompanySearchHit) {
    setOpen(false);
    setError(null);
    setUnsupportedType(null);
    setSubmitLoading(true);
    setQ(`${hit.title} (${hit.company_number})`);
    try {
      const res = await api.lookupCompany(hit.company_number);
      if (!res.eligibility.eligible) {
        // Unsupported company types (CIC, LLP, charity, PLC, …) get a
        // friendly popup with an email CTA instead of a red banner —
        // these customers are still valuable, we just do the filing
        // manually.
        if (res.eligibility.code === 'unsupported-type') {
          setUnsupportedType(res.eligibility.companyType ?? '');
          return;
        }
        setError(res.eligibility.reason || 'Company not eligible');
        return;
      }
      navigate(`/confirm/${res.company.company_number}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lookup failed');
    } finally {
      setSubmitLoading(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || items.length === 0) {
      if (e.key === 'Enter' && q.trim()) {
        // Allow direct submit if the user typed a number and pressed Enter.
        if (/^[A-Z0-9]{6,10}$/i.test(q.trim())) {
          chooseCompany({
            company_number: q.trim().toUpperCase(),
            title: q.trim().toUpperCase(),
            company_status: 'active',
          });
        }
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = items[highlight];
      if (hit) chooseCompany(hit);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  // ── TEMPORARY: pre-launch maintenance notice ──────────────────────────
  // Service goes fully live tomorrow night. Until then, route anyone who
  // clicks "File now" to an email-us notice instead of the live wizard.
  // To re-enable filing: revert this block and restore the original card.
  const MAINTENANCE_MODE = false;
  if (MAINTENANCE_MODE) {
    return (
      <WizardLayout step="search">
        <div className="card text-center">
          <h1 className="text-2xl font-semibold text-slate-900">
            We're putting the final touches on the service
          </h1>
          <p className="mt-3 text-sm text-slate-600">
            Our online filing flow is undergoing brief maintenance and will be
            available again very soon. In the meantime, please email us with
            your company details and we'll get back to you straight away.
          </p>

          <a
            href="mailto:contact.dormantfiling@gmail.com?subject=Dormant%20accounts%20filing%20enquiry"
            className="btn-primary mt-6 inline-flex"
          >
            Email us
          </a>

          <p className="mt-4 text-xs text-slate-500">
            <a
              href="mailto:contact.dormantfiling@gmail.com"
              className="text-brand-700 hover:underline"
            >
              contact.dormantfiling@gmail.com
            </a>
          </p>

          <p className="mt-6 text-xs text-slate-400">
            Thank you for your patience.
          </p>
        </div>
      </WizardLayout>
    );
  }

  return (
    <WizardLayout step="search">
      <div className="card">
        <h1 className="text-2xl font-semibold text-slate-900">Find your company</h1>
        <p className="mt-2 text-sm text-slate-600">
          Start typing your company name or registration number – we'll find it on Companies House.
        </p>

        <div ref={containerRef} className="relative mt-6">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => items.length > 0 && setOpen(true)}
            placeholder="e.g. Acme Trading Ltd, or 12345678"
            className="input"
            autoFocus
            autoComplete="off"
            spellCheck={false}
          />
          {loading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
              Searching…
            </div>
          )}

          {open && items.length > 0 && (
            <ul className="absolute z-10 mt-1 max-h-96 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
              {items.map((hit, i) => {
                const isHl = i === highlight;
                return (
                  <li
                    key={hit.company_number}
                    onMouseEnter={() => setHighlight(i)}
                    onMouseDown={(e) => {
                      e.preventDefault(); // keep input focused
                      chooseCompany(hit);
                    }}
                    className={`cursor-pointer border-b border-slate-100 px-4 py-3 last:border-b-0 ${
                      isHl ? 'bg-brand-50' : 'bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate font-medium text-slate-900">
                          {hit.title}
                          {statusPill(hit.company_status)}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-slate-500">
                          {hit.company_number}
                          {hit.date_of_creation && ` · incorporated ${hit.date_of_creation}`}
                          {hit.address_snippet && ` · ${hit.address_snippet}`}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {open && !loading && items.length === 0 && q.trim().length >= 2 && (
            <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500 shadow-lg">
              No matching companies found.
            </div>
          )}
        </div>

        {submitLoading && (
          <p className="mt-4 text-sm text-slate-500">Looking up your company…</p>
        )}
        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        <p className="mt-6 text-xs text-slate-500">
          Tip: you can also paste a Companies House number directly (e.g. <code>11574942</code>)
          and press Enter.
        </p>
      </div>

      {unsupportedType !== null && (
        <UnsupportedTypeModal
          typeSlug={unsupportedType}
          onClose={() => {
            setUnsupportedType(null);
            setQ('');
          }}
        />
      )}
    </WizardLayout>
  );
}

// ── Unsupported-company-type popup ────────────────────────────────────
// Shown when someone tries to file dormant accounts for a CIC, LLP,
// charity, PLC, or any other type we don't yet support online. Filing
// for these entity types uses a different Companies House form (e.g.
// AA02 for CICs, LL AA01 for LLPs) so we handle them manually via
// email until we build first-class support.

function UnsupportedTypeModal({
  typeSlug,
  onClose,
}: {
  typeSlug: string;
  onClose: () => void;
}) {
  const CONTACT_EMAIL = 'contact.dormantfiling@gmail.com';
  const humanType = friendlyTypeName(typeSlug);
  const subject = `Filing request — ${typeSlug || 'unsupported entity type'}`;
  const body = `Hi,\n\nI'd like to file accounts for ${humanType}. Please let me know how to proceed.\n\nCompany name: \nCompany number: \n\nThanks`;
  const gmailComposeUrl =
    `https://mail.google.com/mail/?view=cm&fs=1` +
    `&to=${encodeURIComponent(CONTACT_EMAIL)}` +
    `&su=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(body)}`;
  const mailtoUrl =
    `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(body)}`;

  // Whether we showed the "email copied" hint after the primary click.
  // mailto: silently no-ops on desktops with no default mail client, so
  // we always copy the address to the clipboard in parallel and tell
  // the user it's ready to paste.
  const [copied, setCopied] = useState(false);

  const handleEmailClick = async () => {
    // Copy the address so the user can paste it into whichever
    // email tool they actually use (Gmail web, Outlook web, etc.).
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setCopied(true);
    } catch {
      // Clipboard blocked (permissions, insecure origin, older
      // browser) — fall through, the address is still visible below.
    }
    // Try to open the user's default mail app in a new tab. If they
    // have one configured, it'll open a composed draft; if not, the
    // tab just closes and the copied address is still there.
    window.open(mailtoUrl, '_blank');
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="unsupported-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-8 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-white p-7 shadow-2xl ring-1 ring-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-2xl shadow-[0_8px_20px_-6px_rgba(15,23,42,0.35),inset_0_1px_0_rgba(255,255,255,0.4)]">
          <span aria-hidden>✉️</span>
        </div>

        <h2 id="unsupported-title" className="mt-4 text-lg font-semibold text-slate-900">
          We file these manually
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          Our online flow currently supports private limited companies (LTDs)
          only. Because you're filing for {humanType}, we handle these filings
          manually — please drop us an email with your company details and
          our team will assist you right away.
        </p>

        <button
          type="button"
          onClick={handleEmailClick}
          className="btn-primary mt-6 inline-flex w-full items-center justify-center"
        >
          Email our team
        </button>

        <a
          href={gmailComposeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex w-full items-center justify-center rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Open in Gmail
        </a>

        {copied && (
          <p className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-center text-xs text-emerald-800">
            Email address copied — paste it into whichever email app you use.
          </p>
        )}

        <p className="mt-3 text-center text-xs text-slate-500">
          Or reach us directly at{' '}
          <a
            href={mailtoUrl}
            className="text-brand-700 hover:underline"
          >
            {CONTACT_EMAIL}
          </a>
        </p>
      </div>
    </div>
  );
}
